import type { LucideIcon, LucideProps } from 'lucide-react';

/**
 * Icon — thin wrapper around Lucide icons that enforces the house
 * sizing scale (DESIGN.md §7: "use Lucide icons consistently, outline
 * style, 16–22px"). Pass the icon as a component, not by name.
 *
 *   import { Mic } from 'lucide-react';
 *   <Icon icon={Mic} size="md" />
 */
export type IconSize = 'sm' | 'md' | 'lg' | number;

export interface IconProps extends Omit<LucideProps, 'size' | 'ref'> {
  icon: LucideIcon;
  size?: IconSize;
}

const SIZE_TABLE: Record<Exclude<IconSize, number>, number> = {
  sm: 14,
  md: 18,
  lg: 22,
};

function resolveSize(size: IconSize): number {
  return typeof size === 'number' ? size : SIZE_TABLE[size];
}

export default function Icon({
  icon: IconComponent,
  size = 'md',
  strokeWidth = 2,
  'aria-hidden': ariaHidden = true,
  ...rest
}: IconProps) {
  const px = resolveSize(size);
  return (
    <IconComponent
      width={px}
      height={px}
      strokeWidth={strokeWidth}
      aria-hidden={ariaHidden}
      {...rest}
    />
  );
}
